"use client"

import { Box, colors, Grid2 as Grid, SxProps, Typography, useMediaQuery, useTheme } from "@mui/material"
import MediaV2 from "@v2/components/MediaV2"
import FlexBox from "@v2/components/styled/FlexBox"
import TextOverflow from "@v2/components/styled/TextOverflow"
import { numberFormat, uniqueByKey } from "@v2/helpers/optimizedHelpers"
import useTouchable from "@v2/hooks/useTouchable"
import { uiColors } from "@v2/lib/color"
import dayjs from "@v2/lib/dayjs"
import Link from "next/link"
import { Eye, Gem, Glasses, Hexagon, Layers, LucideIcon, Minimize2, MonitorSmartphone, Sun, SunMoon, Waves } from "lucide-react"
import { Fragment, memo, PointerEvent, useCallback, useEffect, useRef, useState } from "react"
import { flushSync } from "react-dom"
import { buildItem, trackSelectItem } from "@v2/lib/analytics"
import { OptimumColorType, PLPProduct, PLPSiteProduct } from "../types"

// Every thumbnail is asked for at 470x313 and is drawn at the full width of the card, so
// the frame follows that crop with a little height added on top of it. The square frame it
// replaces left a quarter of the card empty; a frame of exactly 470x313 would put the
// glasses against both edges. This keeps the photo at the size it was cropped for and gives
// it a thin band above and below - about 17px on a desktop card, 8px on a phone.
const IMAGE_RATIO = '470 / 345';

const ZWNJ = '\u200c';
// A model or colour code holds both letters and digits, e.g. RB4547 or SPLL34.
const CODE_TOKEN = /^(?=\S*[A-Za-z])(?=\S*\d)[A-Za-z0-9/._-]+$/;

function normalizeSpaces(text: string) {
    return text.replace(new RegExp(ZWNJ, 'g'), ' ').replace(/\s+/g, ' ').trim();
}

// Variant names read "{model} {colour}", or "{model}-{colour}" when the space is missing,
// or just the model. 97% of the variants under one site product agree on the model.
function modelCode(variantName: string) {
    const name = normalizeSpaces(variantName);
    if (name.includes(' ')) return name.split(' ')[0];
    if (name.includes('-')) return name.split('-')[0];
    return name;
}

// Titles read "{persian description} - {latin brand and model}". Only the persian half is
// kept, minus the brand, because the card already sits under the brand and the model is
// appended from the variant. "RAY-BAN" carries a bare dash, so the separator needs spaces.
function buildCardLabel(title: string, brandFa?: string, brandEn?: string, variantName?: string): { descriptor: string, model: string } {
    const matches = [...title.matchAll(/\s+-\s+/g)];
    const last = matches[matches.length - 1];
    let descriptor = last?.index === undefined ? title : title.slice(0, last.index);
    descriptor = normalizeSpaces(descriptor);

    for (const brand of [brandFa, brandEn]) {
        const value = normalizeSpaces(brand ?? '');
        if (!value) continue;

        for (const form of [value, value.replace(/ /g, '')]) {
            descriptor = descriptor.replace(new RegExp(`(?<!\\S)${form.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?!\\S)`, 'gi'), ' ');
        }
    }

    // Codes left inside the description would repeat the model that follows it.
    descriptor = normalizeSpaces(descriptor).split(' ').filter((word) => word && !CODE_TOKEN.test(word)).join(' ');

    // A lens variant is named in persian - "زایس بلوگارد" - so its first word is a word,
    // not a model. Only a real code earns the "مدل" it would be read after.
    const code = variantName ? modelCode(variantName) : '';
    const model = CODE_TOKEN.test(code) ? code : '';

    // Nothing survived the trimming, so the whole title is better than a blank card.
    if (!descriptor && !model) return { descriptor: normalizeSpaces(title), model: '' };

    return { descriptor, model };
}

// "showProductTitle" heads the card with the site product title - the type, the shape and
// the model - instead of the brand. "showBrand" then puts the brand back, on a line of its
// own under the title: a mixed listing is scanned by brand, but the title is clamped at two
// lines and folding the brand into it is what pushes the model out of sight. A brand page
// names the brand everywhere already, so it takes the title alone.
export default function PlpSiteProduct({ siteProduct, imagePriorityIndex, listId, listName, listIndex, showProductTitle = false, showBrand = false, rounded = false }: {
    siteProduct: PLPSiteProduct,
    imagePriorityIndex?: number;
    listId?: string;
    listName?: string;
    listIndex?: number;
    showProductTitle?: boolean;
    showBrand?: boolean;
    // The brand page surrounds the grid with rounded surfaces -- the hero cover, the
    // category cards, the family cards -- where a square card is the odd one out. The
    // plain listing pages have no such neighbours and stay square.
    rounded?: boolean;
}) {
    const isTouchable = useTouchable()
    const theme = useTheme()
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
    const imagePriority = imagePriorityIndex !== undefined && imagePriorityIndex < (isDesktop ? 3 : 4)
    const [showSecondImage, setShowSecondImage] = useState(false)
    const [imageHoverSuppressed, setImageHoverSuppressed] = useState(false)
    const showSecondImageTimerRef = useRef<number | null>(null)
    const hideSecondImageTimerRef = useRef<number | null>(null)
    const suppressImageHoverRef = useRef(false)
    const secondImageLoadedRef = useRef(false)
    const showSecondImageWhenLoadedRef = useRef(false)
    const productRef = useRef<HTMLAnchorElement | null>(null)
    // const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
    const showProduct = siteProduct.details[0] // sorted by back-end
    const cardLabel = buildCardLabel(siteProduct.title, siteProduct.brand?.fa_name, siteProduct.brand?.en_name, showProduct.name)
    const cardAttributeItems = buildCardAttributeItems(showProduct.card_attributes)
    // A row card takes the whole row on a phone, photo on the right and the text beside it. The
    // back-end decides from the product class (general.row_card_product_class_slugs).
    const rowCard = siteProduct.card_layout === 'row'
    const rowCardSx = (sx: SxProps) => rowCard ? { [theme.breakpoints.down('sm')]: sx } : {}
    const isInStock = showProduct.need_inquiry || (showProduct.final_price > 0 && showProduct.quantity > 0)
    const discountPercent = (showProduct.discount / showProduct.price) * 100;
    const hasUpcomingDiscount = showProduct.discount_object ? dayjs().isBefore(dayjs.unix(showProduct.discount_object?.available_at)) : false;

    const clearImageTimer = useCallback((timerRef: { current: number | null }) => {
        if (timerRef.current === null) return;
        window.clearTimeout(timerRef.current)
        timerRef.current = null
    }, [])

    const handleImagePointerEnter = useCallback((event: PointerEvent<HTMLDivElement>) => {
        if (event.pointerType !== 'mouse' || !showProduct.images[1] || suppressImageHoverRef.current) return;

        clearImageTimer(hideSecondImageTimerRef)
        if (showSecondImage || showSecondImageTimerRef.current !== null) return;

        showSecondImageTimerRef.current = window.setTimeout(() => {
            showSecondImageTimerRef.current = null
            if (secondImageLoadedRef.current) {
                setShowSecondImage(true)
                return;
            }

            showSecondImageWhenLoadedRef.current = true
        }, 500)
    }, [clearImageTimer, showProduct.images, showSecondImage])

    const handleImagePointerLeave = useCallback((event: PointerEvent<HTMLDivElement>) => {
        if (event.pointerType !== 'mouse') return;

        clearImageTimer(showSecondImageTimerRef)
        clearImageTimer(hideSecondImageTimerRef)
        showSecondImageWhenLoadedRef.current = false
        if (suppressImageHoverRef.current) return;

        hideSecondImageTimerRef.current = window.setTimeout(() => {
            hideSecondImageTimerRef.current = null
            if (document.visibilityState !== 'visible') return;

            setShowSecondImage(false)
        }, 150)
    }, [clearImageTimer])

    const handleProductPointerDown = useCallback(() => {
        clearImageTimer(showSecondImageTimerRef)
        clearImageTimer(hideSecondImageTimerRef)
        showSecondImageWhenLoadedRef.current = false
        suppressImageHoverRef.current = true

        flushSync(() => {
            setImageHoverSuppressed(true)
        })
    }, [clearImageTimer])

    // Reported on the click, never on the pointer down. A pointer landing on a
    // card is far more often the start of a scroll than a choice - on a phone
    // the whole page is cards - and counting those made select_item several
    // times the real number. The card navigates with next/link, so the page is
    // never unloading underneath the handler and the click always gets through.
    const handleProductSelect = useCallback(() => {
        if (!listId || !listName) return;

        trackSelectItem(buildItem({
            variantId: showProduct.product_id,
            name: showProduct.name,
            brand: siteProduct.brand?.en_name,
            tomanPrice: showProduct.price,
            tomanDiscount: showProduct.discount,
            siteProductId: siteProduct.id,
            index: listIndex,
            listId,
            listName,
        }), listId, listName)
    }, [listId, listName, listIndex, showProduct, siteProduct])

    const handleSecondImageLoad = useCallback(() => {
        secondImageLoadedRef.current = true
        if (!showSecondImageWhenLoadedRef.current || suppressImageHoverRef.current) return;

        showSecondImageWhenLoadedRef.current = false
        setShowSecondImage(true)
    }, [])

    useEffect(() => () => {
        clearImageTimer(showSecondImageTimerRef)
        clearImageTimer(hideSecondImageTimerRef)
    }, [clearImageTimer])

    useEffect(() => {
        if (!imageHoverSuppressed) return;

        const releaseHoverSuppression = (event: globalThis.PointerEvent) => {
            const elementAtPointer = document.elementFromPoint(event.clientX, event.clientY)
            if (elementAtPointer && productRef.current?.contains(elementAtPointer)) return;

            suppressImageHoverRef.current = false
            setImageHoverSuppressed(false)
            setShowSecondImage(false)
        }

        document.addEventListener('pointermove', releaseHoverSuppression, { passive: true })
        return () => document.removeEventListener('pointermove', releaseHoverSuppression)
    }, [imageHoverSuppressed])

    return (
        <Grid size={{ xs: 12, xs2: rowCard ? 12 : 6, sm: 6, md: 6, lg: 4 }} key={siteProduct.id} sx={{
            // A column, so the text below the photo can take the height the row gives the
            // card and keep the price on the same line as its neighbours' when one card has
            // a line the others lack, such as the attribute row of a lens.
            display: 'flex',
            flexDirection: 'column',
            ...rowCardSx({ flexDirection: 'row', alignItems: 'center' }),
            // Two cards share a phone row, so a card left alone in its row - one between two
            // row cards, or the last of an odd run - grows to the full width instead of
            // leaving half the row empty.
            ...(!rowCard ? { [theme.breakpoints.down('sm')]: { flexGrow: 1 } } : {}),
            transition: '250ms',
            bgcolor: '#fff',
            // Clipped so the image, which sits flush against the top edge, cannot put its
            // own square corners back over the rounded ones.
            ...(rounded ? { borderRadius: 1, overflow: 'hidden' } : {}),
            position: 'relative',
            color: 'inherit',
            textDecoration: 'none',
            ...(isTouchable ? {
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    zIndex: -1,
                    inset: 0,
                }
            } : {}),
            ...(!imageHoverSuppressed ? { '&:hover': {
                zIndex: 20,
                transform: 'translateY(-4px)',
                boxShadow: "rgba(50, 50, 93, 0.15) 0px 30px 60px -12px, rgba(0, 0, 0, 0.2) 0px 18px 36px -18px;",
            }} : {})
        }}
            ref={productRef}
            component={Link}
            data-plp-product-link
            // target={"_blank"}
            // target={!isMobile ? "_blank" : undefined} // TODO | uncomment
            onPointerDown={handleProductPointerDown}
            onClick={handleProductSelect}
            // No pid: the PDP picks the same variant on its own now that both sides
            // rank by stock, then the curated priority, then the newest row.
            href={`/fa/products/${(siteProduct.slug || siteProduct.id)}`}>

            {discountPercent > 0 && isInStock && (
                <Box sx={{
                    position: 'absolute',
                    // The photo is painted after this box and now fills the frame, so without
                    // a layer of its own the badge ends up behind the glasses.
                    zIndex: 1,
                    pointerEvents: 'none',
                    top: {
                        xs: 8,
                        sm: 16
                    },
                    right: {
                        xs: 8,
                        sm: 16
                    },
                }}>
                    <Typography component={'span'} className="en-num" sx={{
                        color: uiColors.primary,
                        fontSize: 13,
                        mr: '4px',
                        [theme.breakpoints.down('sm')]: {
                            fontSize: 11
                        },
                    }}>تخفیف</Typography>
                    <Typography component={'span'} sx={{
                        bgcolor: uiColors.primary,
                        borderRadius: '4px',
                        color: '#fff',
                        fontSize: 13,
                        py: '2px',
                        px: '5px',
                        [theme.breakpoints.down('sm')]: {
                            fontSize: 11
                        },
                    }}>
                        %{discountPercent.toFixed(0)}
                    </Typography>

                </Box>
            )}
            {/* <Box sx={{
                bgcolor: '#fff',
                aspectRatio: '1 / 1',
                display: 'flex',
                alignItems: 'center'
            }}> */}
            <Box sx={{
                position: 'relative',
                aspectRatio: IMAGE_RATIO,
                overflow: 'hidden',
                // As tall as the row, with the photo centred in it, so the colour line at its
                // foot can sit level with the brand and price line of the text beside it.
                ...rowCardSx({
                    width: '40%', flexShrink: 0, alignSelf: 'stretch', aspectRatio: 'auto',
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                }),
                [theme.breakpoints.up('md')]: {
                    '& > .first-image': {
                        transition: imageHoverSuppressed ? 'none' : '550ms',
                        transitionDelay: '0ms',

                        filter: 'blur(0px)'
                    },
                    '& > .second-image': {

                        transition: imageHoverSuppressed ? 'none' : '550ms',
                        transitionDelay: '0ms',

                        opacity: 0,
                        filter: 'blur(5px)'

                    },
                    ...(showSecondImage && showProduct.images[1] ? {
                        '& > .first-image': {
                            opacity: 0,
                            filter: 'blur(5px)',
                        },
                        '& > .second-image': {
                            opacity: 1,
                            filter: 'blur(0px)',
                        },
                    } : {})
                },
            }}
                onPointerEnter={handleImagePointerEnter}
                onPointerLeave={handleImagePointerLeave}>
                <MediaV2
                    media_id={showProduct.images[0]?.id}
                    alt={showProduct.images[0]?.alt ?? `${siteProduct.title} - ${showProduct.name}`}
                    withLoading={!imagePriority}
                    width={470}
                    height={313}
                    imageProps={{
                        // fill: true,
                        style: {
                            objectFit: 'contain',
                        },
                        priority: imagePriority ? true : false,
                        loading: imagePriority ? 'eager' : "lazy",
                        // fetchPriority: imagePriority ? 'auto' : 'auto'
                    }}
                    boxProps={{
                        className: 'first-image',
                        sx: {
                            width: '100%',
                            aspectRatio: IMAGE_RATIO,
                        }
                    }}
                    options={{
                        action: 'cropThumbnail',
                        quality: 82
                    }} />
                {!!showProduct.images[1] && (
                    <MediaV2
                        media_id={showProduct.images[1]?.id}
                        alt={showProduct.images[1]?.alt ?? `${siteProduct.title} - ${showProduct.name}`}
                        // withLoading
                        width={470}
                        height={313}
                        imageProps={{
                            style: {
                                objectFit: 'contain',
                            },
                            loading: 'lazy',
                            onLoad: handleSecondImageLoad,
                        }}
                        boxProps={{
                            className: 'second-image',
                            sx: {
                                position: 'absolute',
                                inset: 0,
                                width: '100%',
                                aspectRatio: IMAGE_RATIO,
                                display: {
                                    xs: 'none',
                                    md: 'flex',
                                },
                            }
                        }}
                        options={{
                            action: 'cropThumbnail',
                            quality: 82
                        }} />
                )}
                {!!siteProduct.tag && (
                    // Top right of the photo; the top left belongs to the discount. The rtl
                    // transform turns left into right.
                    <PLPProductTag tag={siteProduct.tag} sx={{
                        position: 'absolute',
                        zIndex: 2,
                        top: 16,
                        left: 16,
                        [theme.breakpoints.down('sm')]: {
                            top: 8,
                            left: 8,
                            fontSize: 10,
                            px: 1,
                        },
                    }} />
                )}
                {showProductTitle && (
                    // Same inset on both sides, so the rtl transform has nothing to flip.
                    <Box sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 8,
                        right: 8,
                        display: 'flex',
                        alignItems: 'center',
                        pointerEvents: 'none',
                        // The text box beside a row card's photo has 12 below its last line.
                        ...rowCardSx({ bottom: 12 }),
                    }}>
                        <PLPProductColors products={siteProduct.details} sx={{ marginInlineStart: 'auto' }} />
                    </Box>
                )}
            </Box>
            {/* </Box> */}
            <Box sx={{
                // background: `linear-gradient(23deg, ${colors.grey[600]}, transparent 24%)`,
                // borderRadius: '0 0 6px 6px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                flexGrow: 1,
                aspectRatio: '3 / 1', width: '100%', p: {
                    xs: 1, md: 3
                },
                ...rowCardSx({ aspectRatio: 'auto', width: 'auto', minWidth: 0, alignSelf: 'stretch', py: 1.5 }),
            }}>
                <FlexBox sx={{
                    justifyContent: 'space-between',
                    gap: 1,
                }}>
                    {showProductTitle ? (
                        <Typography sx={{
                            minWidth: 0,
                            fontSize: 15,
                            fontWeight: 500,
                            color: colors.grey[900],
                            lineHeight: 1.7,
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                            // Two lines either way, so the price stays level across a row. A card
                            // with chips lets them follow a one-line title straight away instead;
                            // its price is held at the bottom by the row it sits in.
                            minHeight: cardAttributeItems.length || rowCard ? undefined : 'calc(2 * 1.7em)',
                            [theme.breakpoints.down('sm')]: {
                                fontSize: 13,
                            }
                        }}>
                            {cardLabel.descriptor}
                            {cardLabel.model && (
                                <Fragment>
                                    {cardLabel.descriptor ? ' ' : ''}مدل{' '}
                                    <Box component="span" className="en-num" sx={{ whiteSpace: 'nowrap' }}>{cardLabel.model}</Box>
                                </Fragment>
                            )}
                        </Typography>
                    ) : (
                        <PLPProductBrand brand={siteProduct.brand} />
                    )}
                    {!showProductTitle && <PLPProductColors products={siteProduct.details} />}
                </FlexBox>

                {/* Under a product title the chips follow the title straight away, above the
                    brand; under a brand heading they follow the variant name below. */}
                {showProductTitle && <PLPCardAttributes items={cardAttributeItems} />}

                {/* The model already sits at the end of the title, so this line carries the
                    brand instead of repeating it. */}
                {showProductTitle && showBrand && <PLPProductBrand secondary brand={siteProduct.brand} sx={rowCardSx({ display: 'none' })} />}

                {!showProductTitle && (
                    <TextOverflow className="en-num" sx={{
                        fontSize: 16,
                        fontWeight: 300,
                        direction: 'rtl',
                        textAlign: 'left',
                        width: 1,
                        color: colors.grey[600],
                        // mt: 1,
                        [theme.breakpoints.down('sm')]: {
                            fontSize: 13
                        }
                    }}>{showProduct.name}</TextOverflow>
                )}
                {!showProductTitle && <PLPCardAttributes items={cardAttributeItems} />}
                <Box sx={{
                    // The free height of a card shorter than its row goes above the price.
                    mt: 'auto',
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center',
                    // The price goes to the bottom left of the row.
                    // Packed to the left, so the price stays there on a card without a brand;
                    // a brand, when there is one, takes the rest of the line on the right.
                    ...rowCardSx({ gap: 1, justifyContent: 'flex-end' }),
                }}>
                    {/* On a row card the brand moves here, across from the price, where both
                        of its names fit. */}
                    {rowCard && showProductTitle && showBrand && (
                        <PLPProductBrand secondary full brand={siteProduct.brand} sx={{
                            display: 'none',
                            // Sized like the price number and a shade darker than the brand line.
                            // One line that wraps into hidden space, so a latin name too long for
                            // the room left by the price drops out whole instead of being cut.
                            ...rowCardSx({
                                display: 'flex', flexWrap: 'wrap', flexGrow: 1, minWidth: 0, height: 20, lineHeight: '20px', overflow: 'hidden',
                                fontSize: 13, color: colors.grey[800],
                            }),
                        }} />
                    )}
                    <Box>
                        {isInStock && !showProduct.need_inquiry && (

                            <Typography component="span" sx={{
                                fontSize: 16,
                                color: colors.grey[900],
                                fontWeight: 500,
                                letterSpacing: 1,
                                mr: {
                                    xs: '2px',
                                    sm: 1
                                },
                                [theme.breakpoints.down('sm')]: {
                                    fontSize: 13
                                }
                            }}>
                                {String(numberFormat(showProduct.final_price)).replaceAll(',', '.')}
                            </Typography>
                        )}
                        {isInStock ? (
                            <>
                                {showProduct.need_inquiry ? (
                                    <Typography component="span" sx={{
                                        fontSize: 16,
                                        fontWeight: 500,
                                        // The blue the related products and the sliders give it.
                                        color: colors.blue[900],
                                        [theme.breakpoints.down('sm')]: {
                                            fontSize: 12,
                                        }
                                    }}>
                                        نیازمند استعلام
                                    </Typography>
                                ) : (
                                    <Typography component="span" sx={{
                                        fontSize: 13,
                                        fontWeight: 300,
                                        color: colors.grey[800],
                                        [theme.breakpoints.down('sm')]: {
                                            fontSize: 11,
                                        }
                                    }}>
                                        تومانء
                                    </Typography>
                                )}
                            </>
                        ) : (
                            <Typography component="span" sx={{
                                fontSize: 18,
                                fontWeight: 500,
                                color: colors.grey[700],
                                [theme.breakpoints.down('sm')]: {
                                    fontSize: 13,
                                }
                            }}>
                                نـامــوجـود
                            </Typography>
                        )}
                    </Box>
                    {isInStock && hasUpcomingDiscount && (
                        <Box>
                            <Typography component={'span'} className="en-num" sx={{
                                color: uiColors.primary,
                                fontSize: 13,
                                mr: '4px',
                                [theme.breakpoints.down('sm')]: {
                                    display: 'none',
                                },
                            }}>تخفیف تا</Typography>
                            <Typography component={'span'} sx={{
                                bgcolor: uiColors.primary,
                                borderRadius: '4px',
                                color: '#fff',
                                fontSize: 13,
                                py: '2px',
                                px: '5px',
                                [theme.breakpoints.down('sm')]: {
                                    fontSize: 11
                                },
                            }}>
                                %{showProduct.discount_object.value}
                            </Typography>
                        </Box>
                    )}
                    {(isInStock && showProduct.price !== showProduct.final_price && !showProduct.need_inquiry) && (
                        <Typography component="span" sx={{
                            display: {
                                xs: 'none',
                                sm: 'block'
                            },
                            fontSize: 13,
                            color: colors.red[900],
                            fontWeight: 400,
                            letterSpacing: 0,
                            textDecoration: 'line-through',
                            position: 'relative',
                            top: 3,
                            // mr: 1,
                            [theme.breakpoints.down('sm')]: {
                                fontSize: 11
                            }
                        }}>
                            {String(numberFormat(showProduct.price)).replaceAll(',', '.')}
                        </Typography>
                    )}
                </Box>
                <FlexBox sx={{ justifyContent: 'space-between' }}>
                    {(isInStock && showProduct.price !== showProduct.final_price && !showProduct.need_inquiry) && (
                        <Typography component="span" sx={{
                            display: {
                                xs: 'block',
                                sm: 'none'
                            },
                            fontSize: 13,
                            color: colors.red[900],
                            fontWeight: 400,
                            letterSpacing: 0,
                            textDecoration: 'line-through',
                            position: 'relative',
                            // top: 3,
                        }}>
                            {String(numberFormat(showProduct.price)).replaceAll(',', '.')}
                        </Typography>
                    )}
                </FlexBox>
            </Box>



        </Grid>
    )
}

// The persian and latin names of the brand, split by a hairline. "secondary" is the form it
// takes under a product title, where it is no longer the heading of the card.
const PLPProductBrand = memo(function PLPProductBrand({ brand, secondary = false, full = false, sx }: {
    brand?: PLPSiteProduct['brand'],
    secondary?: boolean,
    // Keeps the latin name on a phone too, where it is dropped by default.
    full?: boolean,
    sx?: SxProps,
}) {
    const theme = useTheme()
    if (!brand?.fa_name && !brand?.en_name) return null;

    return (
        <FlexBox sx={{
            fontSize: secondary ? 14 : 17,
            color: secondary ? colors.grey[700] : colors.grey[900],
            fontWeight: secondary ? 400 : 600,
            alignItems: 'center',
            [theme.breakpoints.down('sm')]: {
                fontSize: secondary ? 12 : 14,
                fontWeight: secondary ? 400 : 500,
            },
            ...sx,
        }}>
            <Typography component={'span'} sx={{
                fontSize: 'inherit',
                color: 'inherit',
                fontWeight: 'inherit',
                whiteSpace: 'nowrap',
            }}>
                {brand.fa_name}
            </Typography>
            <Box sx={{
                display: full ? 'block' : {
                    xs: 'none',
                    sm: 'block'
                },
                whiteSpace: 'nowrap',
                ml: '8px',
                pl: '8px',
                position: 'relative',
                '&::after': {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: secondary ? 4 : 5,
                    height: secondary ? 12 : 14,
                    width: '1px',
                    bgcolor: colors.grey[400]
                },
            }}>
                <Typography component={'span'} sx={{ fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit' }}>
                    {brand.en_name}
                </Typography>
            </Box>
        </FlexBox>
    )
})

// The icon and label of each attribute id the back-end lists in general.card_attribute_ids.
// A boolean shows its label; a selection shows its value, so it has no label. "skip" holds
// values that say nothing on a card, like the plain index class every basic lens has;
// "only" is the other way round, for an attribute where only a few values are worth a chip,
// like a titanium frame among plastic and metal ones. "tone" gives a chip its own colours;
// gold plating is only on a few special frames.
const CARD_ATTRIBUTES: Record<string, { icon: LucideIcon; label?: string; skip?: string[]; only?: string[]; tone?: CardAttributeTone }> = {
    '28': { icon: Gem, label: 'روکش طلا', tone: { bg: '#f7edd0', text: '#7a5a12', icon: '#a77b1c' } },
    '5': { icon: Waves, label: 'پلاریزه' },
    '18': { icon: Sun, label: 'کاور آفتابی' },
    '15': { icon: Hexagon, only: ['تیتانیوم', 'فیبرکربن', 'چوبی', 'SPX'] },
    '19': { icon: Glasses, only: ['گریف', 'زیرگریف'] },
    '29': { icon: SunMoon, label: 'فتوکرومیک' },
    '21': { icon: MonitorSmartphone, label: 'محافظ نور آبی' },
    '20': { icon: Eye, label: 'ضدبازتاب' },
    '31': { icon: Minimize2, skip: ['معمولی'] },
    '33': { icon: Layers },
};

// One row of chips under the title, one chip per attribute. Every chip is shown: a row wider
// than the card scrolls sideways, and it runs out to the card's edges (the padding of the
// text box is taken back with a negative margin) so the chips slide off the edge of the card
// rather than being cut at the text's margin.
type CardAttributeTone = { bg: string; text: string; icon: string };
type CardAttributeItem = { id: string; Icon: LucideIcon; text: string; tone?: CardAttributeTone };

function buildCardAttributeItems(attributes?: PLPProduct['card_attributes']): CardAttributeItem[] {
    return (attributes ?? []).flatMap(({ id, values }) => {
        const config = CARD_ATTRIBUTES[id];
        if (!config) return [];

        const text = config.label ?? values
            .filter((value) => !config.skip?.includes(value) && (!config.only || config.only.includes(value)))
            .join('، ');
        return text ? [{ id, Icon: config.icon, text, tone: config.tone }] : [];
    });
}

// How far the white fade reaches in from an edge that has chips beyond it.
const CARD_ATTRIBUTES_FADE = 24;

const PLPCardAttributes = memo(function PLPCardAttributes({ items }: { items: CardAttributeItem[] }) {
    const theme = useTheme()
    const rowRef = useRef<HTMLDivElement | null>(null)
    const [fade, setFade] = useState({ left: false, right: false })

    // The row is rtl, so it starts on the right and scrollLeft runs from 0 down to minus
    // the hidden width as it moves left.
    const updateFade = useCallback(() => {
        const row = rowRef.current
        if (!row) return;

        const hidden = row.scrollWidth - row.clientWidth
        const scrolled = Math.abs(row.scrollLeft)
        const next = { left: scrolled < hidden - 1, right: scrolled > 1 }
        setFade((current) => current.left === next.left && current.right === next.right ? current : next)
    }, [])

    useEffect(() => {
        const row = rowRef.current
        if (!row) return;

        updateFade()
        const observer = new ResizeObserver(updateFade)
        observer.observe(row)
        return () => observer.disconnect()
    }, [updateFade, items])

    if (!items.length) return null;

    // In style, not sx: the rtl transform would flip the gradient's sides.
    const mask = fade.left || fade.right
        ? `linear-gradient(90deg, ${fade.left ? 'transparent' : '#000'} 0, #000 ${CARD_ATTRIBUTES_FADE}px, #000 calc(100% - ${CARD_ATTRIBUTES_FADE}px), ${fade.right ? 'transparent' : '#000'} 100%)`
        : undefined

    return (
        <Box ref={rowRef} onScroll={updateFade} style={{ maskImage: mask, WebkitMaskImage: mask }} sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            my: 1,
            // Same as the padding of the text box around it.
            mx: { xs: -1, md: -3 },
            px: { xs: 1, md: 3 },
            overflowX: 'auto',
            overscrollBehaviorX: 'contain',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
                display: 'none',
            },
            [theme.breakpoints.down('sm')]: {
                gap: '4px',
                my: '4px',
            }
        }}>
            {items.map(({ id, Icon, text, tone }) => (
                <FlexBox key={id} sx={{
                    flexShrink: 0,
                    alignItems: 'center',
                    gap: '4px',
                    height: 26,
                    px: 1,
                    borderRadius: '13px',
                    bgcolor: tone?.bg ?? colors.grey[100],
                    whiteSpace: 'nowrap',
                    fontSize: 12,
                    color: tone?.text ?? colors.grey[800],
                    [theme.breakpoints.down('sm')]: {
                        height: 22,
                        px: '6px',
                        borderRadius: '11px',
                        fontSize: 11,
                    },
                    '& svg': {
                        width: 14,
                        height: 14,
                        [theme.breakpoints.down('sm')]: {
                            width: 12,
                            height: 12,
                        },
                    },
                }}>
                    <Icon color={tone?.icon ?? colors.grey[600]} strokeWidth={1.75} />
                    <span>{text}</span>
                </FlexBox>
            ))}
        </Box>
    )
})

// The «جدید» pill, the landing sliders' tag in a quiet grey: it sits on the photo, where
// the sliders' black pill pulled the eye away from the glasses.
const PLPProductTag = memo(function PLPProductTag({ tag, sx }: { tag: string, sx?: SxProps }) {
    return (
        <Box component="span" sx={{
            bgcolor: colors.grey[200],
            color: colors.grey[800],
            fontSize: 11,
            fontWeight: 400,
            lineHeight: 1.6,
            px: 1.5,
            py: 0.25,
            borderRadius: 99,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            ...sx,
        }}>
            {tag}
        </Box>
    )
})

const PLPProductColors = memo(function PLPProductColors({ products, sx }: { products: PLPProduct[], sx?: SxProps }) {
    // The first part's colours: the frame on eyewear, the body on an accessory.
    const groupFrameColors = products.map((product) => {
        const firstPart = product.colors?.[0]?.part;
        return firstPart ? product.colors!.filter(color => color.part === firstPart) : product.frame_colors;
    });
    const groupEntries = groupFrameColors.map(gfc => [gfc.map(fc => fc.id).join('_'), gfc]);
    const uniqueGroupFc = Object.fromEntries(groupEntries)
    const showColors = Object.values(uniqueGroupFc)
    const colorsCount = showColors.length
    return (
        <Box sx={{ ...sx }}>
            <Box sx={{
                display: 'flex',
                direction: 'rtl',
                width: (colorsCount * 20) - ((colorsCount - 1) * 7),
            }}>
                {showColors.map((colors: OptimumColorType[], index) => {
                    const uniqueColors = uniqueByKey(colors, 'id')
                    return (
                        <Box key={index} sx={{
                            height: 20,
                            width: 20,
                            minWidth: 20,
                            overflow: 'hidden',
                            borderRadius: '50%',
                            border: '2px solid #fff',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative',
                            right: (index * 7) * -1,
                        }}>
                            {uniqueColors.map((color) => (
                                <Box key={color.id} component="span" sx={{
                                    flex: 1,
                                    bgcolor: color.code ?? '#808080',
                                    backgroundImage: color.texture ? `url('${process.env.NEXT_PUBLIC_MEDIA_URL}/api/v1/media/${color.texture}?height=38')` : undefined,
                                    backgroundSize: 'cover'
                                }} />
                            ))}
                        </Box>
                    )
                })}
            </Box>
        </Box>
    )
})
